/**
 * Analytics Abstraction Layer
 * Multi-provider support for easy migration
 */

import {
  ANALYTICS_FLAGS,
  ANALYTICS_STORAGE_KEYS,
  AnalyticsControl,
} from "../../constants/analytics";
import type {
  AnalyticsProvider,
  CTAClickedProperties,
  DemoInteractedProperties,
  EmailCapturedProperties,
  EmailCTAClickedProperties,
  EmailValidationFailedProperties,
  InstallIntentCreatedProperties,
  PageViewedProperties,
  PluginInstallClickedProperties,
  PricingViewedProperties,
  SectionViewedProperties,
  ThemeToggledProperties,
  VideoPlayedProperties,
} from "../../types/analytics";
import { AnalyticsEvents } from "../../types/analytics";

import { mirrorEventToGa4 } from "./ga4Mirror";
import { PostHogClient } from "./posthogClient";
import { getViteMetaEnv } from "@/utils/viteMetaEnv";

/**
 * Analytics Manager
 * Manages multiple analytics providers
 */
export class Analytics {
  private providers: AnalyticsProvider[] = [];
  private initialized = false;

  /**
   * Initialize analytics with default providers
   */
  init(): void {
    if (this.initialized) {
      return;
    }

    // Add PostHog as default provider
    const posthog = new PostHogClient();
    this.addProvider(posthog);

    // Note: GA4 loads from initThirdPartyScripts (VITE_GA4_MEASUREMENT_ID). Selected events
    // are mirrored to GA4 from ga4Mirror.ts when analytics.track runs (PostHog remains full-fidelity).

    this.initialized = true;

    if (ANALYTICS_FLAGS.debug) {
      console.log(
        "[Analytics] Initialized with",
        this.providers.length,
        "provider(s)"
      );
    }
  }

  /**
   * Add a new analytics provider
   */
  addProvider(provider: AnalyticsProvider): void {
    this.providers.push(provider);
  }

  /**
   * Generic track method
   * Sends event to all registered providers
   */
  track(event: string, properties?: Record<string, unknown>): void {
    if (!this.initialized) {
      this.init();
    }

    this.providers.forEach((provider) => {
      try {
        provider.capture(event, properties);
      } catch (error) {
        // Silent fail per provider
        if (ANALYTICS_FLAGS.debug) {
          console.warn("[Analytics] Provider error:", provider, error);
        }
      }
    });

    mirrorEventToGa4(event, properties);
  }

  /**
   * Identify user
   */
  identify(userId: string, traits?: Record<string, unknown>): void {
    if (!this.initialized) {
      this.init();
    }

    // Add environment information to user traits
    const enrichedTraits = {
      ...traits,
      environment: getViteMetaEnv().MODE || "production",
      is_development: getViteMetaEnv().DEV || false,
    };

    this.providers.forEach((provider) => {
      try {
        provider.identify(userId, enrichedTraits);
      } catch (error) {
        if (ANALYTICS_FLAGS.debug) {
          console.warn("[Analytics] Provider identify error:", provider, error);
        }
      }
    });
  }

  /**
   * Reset analytics
   */
  reset(): void {
    this.providers.forEach((provider) => {
      try {
        provider.reset();
      } catch (error) {
        if (ANALYTICS_FLAGS.debug) {
          console.warn("[Analytics] Provider reset error:", provider, error);
        }
      }
    });
  }

  /**
   * Alias - link two user identities
   * Used for landing page → plugin conversion tracking
   */
  alias(newId: string, oldId?: string): void {
    if (!this.initialized) {
      this.init();
    }

    this.providers.forEach((provider) => {
      try {
        provider.alias(newId, oldId);
      } catch (error) {
        if (ANALYTICS_FLAGS.debug) {
          console.warn("[Analytics] Provider alias error:", provider, error);
        }
      }
    });
  }

  /**
   * Get current distinct ID (for matching API)
   */
  getDistinctId(): string {
    // Get from PostHog provider
    const posthogProvider = this.providers.find(p => p instanceof PostHogClient);
    if (posthogProvider && 'distinctId' in posthogProvider) {
      return (posthogProvider as PostHogClient).distinctId;
    }
    return '';
  }

  /**
   * Check if this is a first-time event
   */
  isFirstTime(eventKey: string): boolean {
    try {
      const hasOccurred = localStorage.getItem(eventKey);
      if (!hasOccurred) {
        localStorage.setItem(eventKey, "true");
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }

  // Type-safe tracking methods for website events

  trackPageViewed(properties: PageViewedProperties): void {
    this.track(
      AnalyticsEvents.PAGE_VIEWED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackEmailCaptured(properties: EmailCapturedProperties): void {
    const isFirstTime = this.isFirstTime(
      ANALYTICS_STORAGE_KEYS.FIRST_EMAIL_CAPTURED,
    );
    this.track(AnalyticsEvents.EMAIL_CAPTURED, {
      ...properties,
      isFirstTime,
    } as unknown as Record<string, unknown>);
  }

  trackEmailCTAClicked(properties: EmailCTAClickedProperties): void {
    this.track(
      AnalyticsEvents.EMAIL_CTA_CLICKED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackEmailValidationFailed(properties: EmailValidationFailedProperties): void {
    this.track(
      AnalyticsEvents.EMAIL_VALIDATION_FAILED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackCTAClicked(properties: CTAClickedProperties): void {
    this.track(
      AnalyticsEvents.CTA_CLICKED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackDemoInteracted(properties: DemoInteractedProperties): void {
    this.track(
      AnalyticsEvents.DEMO_INTERACTED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackSectionViewed(properties: SectionViewedProperties): void {
    this.track(
      AnalyticsEvents.SECTION_VIEWED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackVideoPlayed(properties: VideoPlayedProperties): void {
    this.track(
      AnalyticsEvents.VIDEO_PLAYED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackPricingViewed(properties: PricingViewedProperties): void {
    this.track(
      AnalyticsEvents.PRICING_VIEWED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackThemeToggled(properties: ThemeToggledProperties): void {
    this.track(
      AnalyticsEvents.THEME_TOGGLED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackInstallIntentCreated(properties: InstallIntentCreatedProperties): void {
    this.track(
      AnalyticsEvents.INSTALL_INTENT_CREATED,
      properties as unknown as Record<string, unknown>,
    );
  }

  trackPluginInstallClicked(properties: PluginInstallClickedProperties): void {
    this.track(
      AnalyticsEvents.PLUGIN_INSTALL_CLICKED,
      properties as unknown as Record<string, unknown>,
    );
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Export runtime controls (for debugging)
export { AnalyticsControl };

// Expose to window in development for easy debugging
if (ANALYTICS_FLAGS.debug && typeof window !== "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).AnalyticsControl = AnalyticsControl;
  console.log(
    "🔧 Debug mode: Use AnalyticsControl.disable() to stop analytics",
  );
  console.log("🔧 Debug mode: Use AnalyticsControl.status() to check status");
}

// Export types
export type { AnalyticsProvider } from "../../types/analytics";
export { AnalyticsEvents } from "../../types/analytics";

